/**
 * MINAKI Signal Layer — global tracker.
 *
 * Loaded once, site-wide, from theme.liquid. Nothing page-specific lives
 * here. Every reusable Shopify section gets tracking by adding
 * data-track-* attributes to its own Liquid file once — see
 * theme-snippet/sections/*.liquid for the pattern. This file just watches
 * the DOM for those attributes and fires the generalized event shapes
 * from tracking-plan/schema.json. Adding a new component never means
 * touching this file.
 */
(function () {
  "use strict";

  // A merchant editing the theme in Shopify's editor is not a real visitor —
  // firing tracked events for their clicks pollutes every downstream number.
  // theme.liquid sets this from {{ request.design_mode }}. Same guard
  // sonishivam1911/shopify-analytics's theme-analytics.js uses, ported here
  // after finding it missing from this file — a real gap, not a style choice.
  if (window.MINAKI_DESIGN_MODE) return;

  // Real bot traffic found by checking actual landed data, not assumed:
  // 93.5% of a day's distinct anonymousIds shared one exact generic
  // "Safari on Mac" UA string, hit one page each and never returned, 95%
  // geo-tagged US on an India-priced INR site — and still generated real
  // element_viewed impressions, which only happens if a real JS engine
  // executed this file and its IntersectionObserver. That rules out plain
  // curl/requests-style scrapers (they never run this script at all) and
  // points straight at headless-browser automation (Puppeteer/Playwright/
  // Selenium) — which, unless a stealth plugin specifically evades it,
  // sets navigator.webdriver = true. Checking a UA string can't catch this
  // traffic (it deliberately looks like a normal browser); this can.
  if (navigator.webdriver) return;

  // Stays under minaki.shop, not minaki.me — see the comment in
  // layout/theme.liquid next to where this global is set. The Set-Cookie
  // this endpoint issues is scoped to .minaki.shop; a browser drops that
  // cookie outright if it didn't come from a minaki.shop-family origin.
  var PROXY_URL = window.MINAKI_TRACK_ENDPOINT || "https://events.minaki.shop/v1/track";
  var FLUSH_INTERVAL_MS = 10000;
  var FLUSH_BATCH_SIZE = 20;
  var SCROLL_CHECKPOINTS = [25, 50, 75, 100];
  // A dropped/failed flush gets requeued (see flush()'s .catch) rather than
  // lost outright — this caps how much a dead network can pile up client-side
  // before we start shedding the oldest events instead of growing forever.
  var QUEUE_MAX_SIZE = 200;

  var queue = [];
  var flushTimer = null;
  var seenScrollCheckpoints = {};
  var observedElements = new WeakSet();
  var resolvedAnonymousId = null;
  var pageLoadedAt = Date.now();
  var exitIntentFired = false;
  var rageClickState = {}; // keyed by stable target key string -> {times: []}

  // theme.liquid sets data-page-type straight off Shopify's own
  // {{ request.page_type }} — a real bug found by checking actual landed
  // rows, not assumed: Shopify's raw values ("index" for the homepage,
  // "customers/login", "404", "list-collections"...) never matched this
  // schema's page_type enum ("home", "account_login", "error_404"...) at
  // all. Every page_type this whole project has ever sent has been
  // Shopify's raw string, not the documented enum. Normalized here, once,
  // rather than in theme.liquid — this mapping is about this tracker's own
  // schema, not the theme's markup.
  var SHOPIFY_PAGE_TYPE_MAP = {
    index: "home",
    product: "product",
    collection: "collection",
    "list-collections": "collection",
    cart: "cart",
    search: "search",
    page: "page_generic",
    policy: "policy",
    blog: "blog_index",
    article: "blog_article",
    "customers/login": "account_login",
    "customers/register": "account_signup",
    "customers/activate_account": "account_signup",
    "customers/reset_password": "account_login",
    "customers/account": "account_order_history",
    "customers/order": "account_order_detail",
    "customers/addresses": "account_order_history",
    404: "error_404",
  };

  function pageType() {
    var raw = document.body.getAttribute("data-page-type") || "page";
    return SHOPIFY_PAGE_TYPE_MAP[raw] || "page_generic";
  }

  // ---- element naming convention ----------------------------------------
  //
  // Every element_viewed/element_clicked lands with a composite element_name
  // — "<page_zone>_<element_type>_<instance>" (e.g.
  // "pdp_product_grid_recommendations", "plp_hero_banner_demi_fine") —
  // alongside the existing element_type/element_id. page_zone is a short
  // code off page_type (PAGE_ZONE below) — "which large page area this
  // happened on" (home/PDP/PLP/blog/policy/...), the way the term was
  // actually meant here.
  //
  // An earlier pass keyed this off a business-vertical global instead
  // (MINAKI_VERTICAL) — wrong axis. That confused this Postgres's literal
  // `realestate` *schema* (a separate MINAKI business) with "page real
  // estate" in the UX sense, which is what this composite key actually
  // needs to encode. Dropped; nothing here reads that global any more.
  //
  // instance prefers a curated list_id (tracking-plan/list-registry.md —
  // "recommendations", "recently_viewed", ...) over a slugified label —
  // the registry name is what a dashboard should group two different PDP
  // rails by; the label is the fallback for elements with no list at all
  // (nav_link, footer_link, trust_badge...).

  var PAGE_ZONE = {
    home: "home",
    collection: "plp",
    product: "pdp",
    cart: "cart",
    search: "search",
    account_login: "account",
    account_signup: "account",
    account_order_history: "account",
    account_order_detail: "account",
    wishlist: "wishlist",
    checkout: "checkout",
    thank_you: "thank_you",
    blog_article: "blog",
    blog_index: "blog",
    policy: "policy",
    page_generic: "page",
    error_404: "error",
  };

  function pageZone() {
    return PAGE_ZONE[pageType()] || "page";
  }

  function slugify(text) {
    if (!text) return undefined;
    var slug = String(text)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return slug || undefined;
  }

  // Same fallback chain outbound_link_clicked/file_downloaded already used
  // for linkText, generalized to every tracked element instead of being
  // reimplemented per-caller. data-track-value (explicit, highest priority)
  // → aria-label → visible text → alt (images) → undefined. This is what
  // turns "nav_link" into "nav_link: Shipping Policy" instead of an opaque
  // category with no name attached.
  function deriveElementLabel(el) {
    var explicit = el.getAttribute("data-track-value");
    if (explicit) return explicit;
    var ariaLabel = el.getAttribute("aria-label");
    if (ariaLabel && ariaLabel.trim()) return ariaLabel.trim();
    var text = (el.textContent || "").trim().replace(/\s+/g, " ").slice(0, 120);
    if (text) return text;
    if (el.tagName === "IMG") {
      var alt = el.getAttribute("alt");
      if (alt && alt.trim()) return alt.trim();
    }
    return undefined;
  }

  // instance is already-slug-shaped when it's a curated list_id (registry
  // names are lowercase_snake by convention) — only run it through
  // slugify() when it looks like free text (a label).
  var SLUG_SHAPED = /^[a-z0-9_]+$/;
  function buildElementName(elementType, instance) {
    var parts = [pageZone(), elementType || "unknown"];
    var instanceSlug = instance && SLUG_SHAPED.test(instance) ? instance : slugify(instance);
    if (instanceSlug) parts.push(instanceSlug);
    return parts.join("_");
  }

  // Per-node stable identity for elements that carry neither a real `id`
  // nor a `data-track-type` — used by rage_click below. A WeakMap keyed by
  // the actual DOM node (not a derived selector string) means two unrelated
  // buttons that happen to share a CSS class (e.g. "btn btn-primary" on
  // every generic CTA in the theme) can never collide into the same rage-
  // click bucket the way a tagName+className string key used to.
  var anonymousElementIds = new WeakMap();
  var anonymousElementCounter = 0;

  function stableTargetKey(el) {
    if (el.id) return "#" + el.id;
    var type = el.getAttribute("data-track-type");
    if (type) return "[data-track-type=" + type + "]";
    if (!anonymousElementIds.has(el)) {
      anonymousElementCounter += 1;
      anonymousElementIds.set(el, "anon_" + anonymousElementCounter);
    }
    return anonymousElementIds.get(el);
  }

  function generateMessageId() {
    return window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : "msg_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2);
  }

  // Shopify exposes the active storefront currency as a global when
  // currency conversion is on, plus a conversion `rate` — "the rate from
  // the shop's own currency to the customer's displayed currency" per
  // Shopify's own docs. A bare price with no currency code, and no way to
  // compare it against a price captured under a different displayed
  // currency, was silently wrong the day a second currency went live —
  // every price-bearing event now carries BOTH numbers: the presentment
  // price/currency (what the shopper actually sees) and the shop-currency
  // equivalent, computed off that rate. undefined (not a guessed rate of
  // 1) when Shopify's currency conversion isn't active at all — same
  // "absent, not faked" convention as everything else in this file.
  //
  // shopCurrency() itself needs window.MINAKI_SHOP_CURRENCY set once in
  // theme.liquid ({{ shop.currency | json }}) — Shopify.currency only
  // exposes the *displayed* code and the conversion rate, never the shop's
  // own base currency code directly, so there's nothing to derive it from
  // client-side without that one extra global.
  function currencyInfo() {
    var sc = window.Shopify && window.Shopify.currency;
    if (!sc || !sc.active) return undefined;
    return { currency: sc.active, rate: sc.rate ? Number(sc.rate) : undefined };
  }

  function activeCurrency() {
    var info = currencyInfo();
    return info ? info.currency : undefined;
  }

  function shopCurrency() {
    return window.MINAKI_SHOP_CURRENCY || undefined;
  }

  function shopCurrencyPrice(presentmentPrice) {
    var info = currencyInfo();
    if (presentmentPrice == null || !info || !info.rate) return undefined;
    return Math.round((presentmentPrice / info.rate) * 100) / 100;
  }

  // ---- Phase 1: identity, page, campaign, device context ---------------

  // theme.liquid sets window.MINAKI_CUSTOMER_ID = {{ customer.id | json }}
  // (null when logged out). Rides at batch level like consent/network —
  // itp-proxy lifts it onto the RudderStack event's top-level userId,
  // not into context, since that's the field RudderStack's own identity
  // resolution actually reads.
  function userId() {
    return window.MINAKI_CUSTOMER_ID || undefined;
  }

  // One id per tab session, not per page — sessionStorage survives
  // reloads/navigations within the tab, cleared when the tab closes.
  // crypto.randomUUID() isn't on old Safari; fall back to a
  // good-enough random string, this is a grouping key, not a security
  // token.
  function sessionId() {
    var existing = sessionStorage.getItem("minaki_session_id");
    if (existing) return existing;
    var id =
      window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : "sess_" + Date.now().toString(36) + "_" + Math.random().toString(36).slice(2);
    sessionStorage.setItem("minaki_session_id", id);
    return id;
  }

  function pageContext() {
    var nav = performance && performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    return {
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer || undefined,
      title: document.title || undefined,
      page_load_ms: nav ? Math.round(nav.loadEventEnd - nav.startTime) || undefined : undefined,
      // A theme-config choice (which header layout variant is actually
      // rendering, which A/B bucket, etc.) changes what a section *is*
      // without changing its element_type/list_id — without this, a
      // settings change silently breaks historical comparability, looking
      // like a real behavior shift instead of a config change. theme.liquid
      // sets this once, e.g. window.MINAKI_SECTION_VARIANTS =
      // { header_design: {{ settings.header_design | json }} } — absent
      // (not an empty object) until that global actually exists.
      section_variants: window.MINAKI_SECTION_VARIANTS || undefined,
    };
  }

  // User-Agent Client Hints, high-entropy tier — Chromium only (Chrome/
  // Edge/Android Chrome/Samsung Internet); Safari and Firefox never
  // shipped navigator.userAgentData at all, so this stays undefined
  // there, same "absent, not null" convention as networkInfo(). Async
  // by spec (a permission-gated browser API), so it's primed once at
  // boot into a module-level cache rather than awaited inline — the
  // first flush or two may go out without it, every flush after the
  // promise resolves carries it. `model` is the one field that gets
  // close to a real hardware model (e.g. "SM-S911B" on Android); no
  // desktop browser exposes a literal machine name, Client Hints
  // included — platform/architecture/bitness is the ceiling there.
  var uaHighEntropy;
  function primeUAHighEntropy() {
    if (!(navigator.userAgentData && navigator.userAgentData.getHighEntropyValues)) return;
    navigator.userAgentData
      .getHighEntropyValues(["model", "platformVersion", "architecture", "bitness"])
      .then(function (data) {
        uaHighEntropy = data;
      })
      .catch(function () {
        /* best-effort — Client Hints permission can be withheld */
      });
  }
  primeUAHighEntropy();

  function deviceContext() {
    return {
      user_agent: navigator.userAgent,
      screen_width: screen.width,
      screen_height: screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      platform: navigator.platform || undefined,
      timezone: (Intl && Intl.DateTimeFormat && Intl.DateTimeFormat().resolvedOptions().timeZone) || undefined,
      ua_mobile: navigator.userAgentData ? navigator.userAgentData.mobile : undefined,
      ua_model: uaHighEntropy && uaHighEntropy.model ? uaHighEntropy.model : undefined,
      ua_platform_version: uaHighEntropy ? uaHighEntropy.platformVersion : undefined,
      ua_architecture: uaHighEntropy ? uaHighEntropy.architecture : undefined,
      ua_bitness: uaHighEntropy ? uaHighEntropy.bitness : undefined,
    };
  }

  // First-touch UTM/click-id capture — read off the landing URL once,
  // persisted for the whole session (sessionStorage, not localStorage:
  // "session" attribution, not lifetime). Later pageviews in the same
  // session keep reporting the original landing params, not overwrite
  // with blank ones on an internal link click.
  var CAMPAIGN_PARAMS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
    "gclid", "fbclid", "ttclid", "msclkid",
  ];

  function captureCampaignContext() {
    if (sessionStorage.getItem("minaki_campaign")) return;
    var params = new URLSearchParams(window.location.search);
    var found = {};
    var any = false;
    CAMPAIGN_PARAMS.forEach(function (key) {
      var value = params.get(key);
      if (value) {
        found[key] = value;
        any = true;
      }
    });
    if (any) sessionStorage.setItem("minaki_campaign", JSON.stringify(found));
  }

  function campaignContext() {
    var raw = sessionStorage.getItem("minaki_campaign");
    if (!raw) return undefined;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return undefined;
    }
  }

  captureCampaignContext();

  // Network Information API — Chromium/Android only (no Safari, no
  // Firefox; iOS Safari never shipped it). undefined on unsupported
  // browsers rather than a fake/zeroed value, so "no data" stays
  // distinguishable from "measured and it's this". Read once per flush,
  // not once per event — it's a device/connection property, not
  // something that changes event-to-event within one batch.
  function networkInfo() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return undefined;
    return {
      effective_type: conn.effectiveType || undefined,
      downlink_mbps: conn.downlink,
      rtt_ms: conn.rtt,
      save_data: conn.saveData,
    };
  }

  // Product fields ride together off one scoped ancestor (product cards,
  // the PDP's variant-form wrapper) — same closest()-based pattern as
  // list_id/position, so any click or impression anywhere inside that
  // wrapper carries full product context without each caller re-deriving
  // it. Price/compare_at_price come through as raw integer cents, same
  // convention the theme's own data-product-options JSON blobs already
  // use (see product-grid-item5.liquid) — no unit conversion here.
  function productContext(el) {
    var scoped = el.closest("[data-track-product-id]");
    if (!scoped) return {};
    var price = scoped.hasAttribute("data-track-product-price")
      ? Number(scoped.getAttribute("data-track-product-price"))
      : undefined;
    return {
      product_id: scoped.getAttribute("data-track-product-id") || undefined,
      product_name: scoped.getAttribute("data-track-product-name") || undefined,
      product_category: scoped.getAttribute("data-track-product-category") || undefined,
      product_sku: scoped.getAttribute("data-track-product-sku") || undefined,
      // variant_id is read fresh at click/view time, same as price — a PDP's
      // own variant-picker JS (not in this repo) is expected to update
      // data-track-variant-id/data-track-product-price/data-track-product-
      // compare-price on this same wrapper whenever the shopper changes
      // variant, before this fires. Same contract the price attributes
      // already relied on; this just extends it rather than adding new
      // plumbing — a variant_option click now carries the *resulting*
      // variant and price automatically, no special-cased handler needed.
      variant_id: scoped.getAttribute("data-track-variant-id") || undefined,
      product_price: price,
      product_price_shop_currency: shopCurrencyPrice(price),
      product_compare_at_price: scoped.hasAttribute("data-track-product-compare-price")
        ? Number(scoped.getAttribute("data-track-product-compare-price"))
        : undefined,
      currency: activeCurrency(),
      shop_currency: shopCurrency(),
    };
  }

  function nearestItemId(el) {
    var scoped = el.closest("[data-track-item-id]");
    return scoped ? scoped.getAttribute("data-track-item-id") : undefined;
  }

  function readListContext(el) {
    var listEl = el.closest("[data-track-list-id]");
    // item_id/position/item_type can live on the clicked element itself, or
    // on a nearer ancestor than the list container — e.g. a JS-populated
    // card (offer-discovery.js clones a <template> at runtime and stamps
    // data-track-item-id onto the card div; the click target is a button
    // nested inside it, not the card itself).
    var itemEl = el.closest("[data-track-item-id], [data-track-position]") || el;
    if (!listEl) {
      return {
        item_type: itemEl.getAttribute("data-track-item-type") || undefined,
      };
    }
    return {
      list_id: listEl.getAttribute("data-track-list-id"),
      list_type: listEl.getAttribute("data-track-list-type") || undefined,
      item_type: itemEl.getAttribute("data-track-item-type") || listEl.getAttribute("data-track-item-type"),
      position: itemEl.hasAttribute("data-track-position")
        ? Number(itemEl.getAttribute("data-track-position"))
        : undefined,
    };
  }

  function enqueue(event, properties) {
    queue.push({
      type: "track",
      event: event,
      // messageId is the idempotency key — RudderStack dedupes on it. A
      // dropped/retried batch (see flush()'s .catch below) can now be
      // resent without double-counting, which was impossible before this:
      // there was no per-event key at all, so a resend was indistinguishable
      // from a genuinely new event.
      messageId: generateMessageId(),
      properties: Object.assign({ page_type: pageType() }, properties),
      timestamp: new Date().toISOString(),
    });
    // Shed oldest first — a flush that's been failing for a while should
    // lose its earliest, stalest events before it loses the newest ones.
    if (queue.length > QUEUE_MAX_SIZE) queue.splice(0, queue.length - QUEUE_MAX_SIZE);
    if (queue.length >= FLUSH_BATCH_SIZE) flush();
    else scheduleFlush();
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }

  // Long-lived (localStorage, not sessionStorage) — session_started already
  // answers "first page of THIS visit"; this answers "has this browser ever
  // been seen at all". Rides at batch level like consent/network/campaign,
  // same reasoning: it doesn't change event-to-event within one batch.
  // undefined (not a guessed false) when localStorage is blocked — private
  // browsing / disabled storage shouldn't silently read as "new visitor".
  function isReturningVisitor() {
    var seen;
    try {
      seen = localStorage.getItem("minaki_returning");
      if (!seen) localStorage.setItem("minaki_returning", "1");
    } catch (err) {
      return undefined;
    }
    return !!seen;
  }

  function flush() {
    clearTimeout(flushTimer);
    flushTimer = null;
    if (queue.length === 0) return;
    var batch = queue.splice(0, queue.length);
    var payload = JSON.stringify({
      batch: batch,
      consent: window.MINAKI_CONSENT_STATE || {},
      network: networkInfo(),
      user_id: userId(),
      session_id: sessionId(),
      page: pageContext(),
      device: deviceContext(),
      campaign: campaignContext(),
      returning_visitor: isReturningVisitor(),
    });

    // sendBeacon has no response/failure signal to act on — accepted as
    // fire-and-forget same as before, it only fires on the tab-hidden path
    // where there's no next flush to retry on anyway.
    if (navigator.sendBeacon && document.visibilityState === "hidden") {
      navigator.sendBeacon(PROXY_URL, new Blob([payload], { type: "application/json" }));
      return;
    }
    fetch(PROXY_URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    })
      .then(function (r) {
        if (!r.ok) throw new Error("itp_proxy_" + r.status);
        return r.json();
      })
      .then(function (data) {
        if (data && data.anonymousId) onAnonymousIdResolved(data.anonymousId);
      })
      .catch(function () {
        // Was a bare swallow — a dropped batch was gone forever, no
        // distinction between "shopper closed the tab" (fine, nothing to
        // do) and "the proxy is down for a minute" (recoverable, shouldn't
        // just vanish). Requeue at the front so retried events replay in
        // their original order ahead of anything queued since; messageId
        // (added in enqueue()) makes the eventual resend safe to dedupe
        // against, so this can't turn into double-counting downstream.
        queue = batch.concat(queue);
        if (queue.length > QUEUE_MAX_SIZE) queue.splice(0, queue.length - QUEUE_MAX_SIZE);
        scheduleFlush();
      });
  }

  // ---- identity bridge -------------------------------------------------
  //
  // The proxy's anonymousId cookie is httpOnly (deliberately — see the
  // KRD's ITP proxy section), so this file can't read it back off
  // document.cookie. It doesn't need to: the proxy echoes the id in every
  // /v1/track response body, which is enough to bridge it onto the order.
  //
  // Buy It Now / Shop Pay render through Shopify's own payment_button
  // filter — opaque, no data-track-* reaches it (see PDP instrumentation
  // notes). This is how that gap actually closes: instead of trying to
  // catch the click, stamp anonymousId onto the cart as a Shopify cart
  // attribute *before* checkout starts, on any path. Whichever button the
  // shopper uses, Shopify carries cart attributes onto the order, and
  // identity resolution reads it from there — no click needed.
  //
  // Ported from sonishivam1911/shopify-analytics's cart-attribute bridge
  // (`_anon_id`/`_session_id` via /cart/update.js) — flagged in that
  // project's own notes as implemented but not yet proven across Shop Pay
  // /PayPal Express/Buy Now/draft orders/POS. Same open risk applies here;
  // don't treat this as settled until it's actually spiked against those
  // paths on a real order.

  function onAnonymousIdResolved(anonymousId) {
    resolvedAnonymousId = anonymousId;
    if (sessionStorage.getItem("minaki_bridged")) return;
    sessionStorage.setItem("minaki_bridged", "1");
    fetch("/cart/update.js", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { _anon_id: anonymousId } }),
      keepalive: true,
    }).catch(function () {
      /* cart attribute bridge is best-effort too — never block checkout */
    });
  }

  // ---- journey attribution (click -> next pageview) ---------------------
  //
  // document.referrer only gives domain-level attribution — "came from
  // minaki.shop" says nothing when both the referrer and the destination
  // are minaki.shop, which is every internal navigation. What actually
  // answers "which product grid, which position, sent this shopper to this
  // PDP" is captured here: at the moment of a same-origin navigation click
  // inside a tracked list, persist the list/item context to sessionStorage
  // — single read, consumed by the very next page_viewed, then deleted.
  // Not session-length state; a one-hop relay across the one moment
  // JavaScript can't otherwise see across (the actual page load).

  var ENTRY_CONTEXT_KEY = "minaki_entry_ctx";

  document.addEventListener("click", function (e) {
    var anchor = e.target.closest("a[href]");
    if (!anchor) return;
    var listEl = anchor.closest("[data-track-list-id]");
    if (!listEl) return; // not a list-originated click — nothing to attribute
    var url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (err) {
      return;
    }
    if (url.origin !== window.location.origin) return; // external nav already covered by outbound_link_clicked
    var listCtx = readListContext(anchor);
    var type = anchor.getAttribute("data-track-type") || undefined;
    var label = deriveElementLabel(anchor);
    try {
      sessionStorage.setItem(
        ENTRY_CONTEXT_KEY,
        JSON.stringify({
          list_id: listCtx.list_id,
          list_type: listCtx.list_type,
          item_type: listCtx.item_type,
          position: listCtx.position,
          element_name: buildElementName(type, listCtx.list_id || label),
          source_page_type: pageType(),
        })
      );
    } catch (err) {
      /* sessionStorage can throw in locked-down contexts (private-mode
         quirks on old Safari) — attribution is additive, never worth
         breaking navigation over */
    }
  });

  function consumeEntryContext() {
    var raw;
    try {
      raw = sessionStorage.getItem(ENTRY_CONTEXT_KEY);
      if (raw) sessionStorage.removeItem(ENTRY_CONTEXT_KEY);
    } catch (err) {
      return undefined;
    }
    if (!raw) return undefined;
    try {
      return JSON.parse(raw);
    } catch (err) {
      return undefined;
    }
  }

  // ---- element_viewed ----------------------------------------------------

  var impressionObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        impressionObserver.unobserve(el); // one impression per element per page load
        var type = el.getAttribute("data-track-type");
        var label = deriveElementLabel(el);
        var listCtx = readListContext(el);
        enqueue(
          "element_viewed",
          Object.assign(
            {
              element_type: type,
              element_id: nearestItemId(el) || el.getAttribute("data-track-list-id") || undefined,
              element_label: label,
              // Prefer the curated list_id over the free-text label — two
              // different PDP rails should group under
              // "pdp_product_grid_recommendations"/"..._recently_viewed",
              // not both collapse into "pdp_list_item_view_all" because
              // that happened to be the visible link text on both.
              element_name: buildElementName(type, listCtx.list_id || label),
            },
            productContext(el),
            listCtx
          )
        );
      });
    },
    { threshold: 0.5 }
  );

  // Real gap found by checking actual landed volume, not assumed: these
  // five element_types alone accounted for ~40% of a day's element_viewed
  // rows (163k of 405k) — because they're page chrome present on every
  // single page, not content whose visibility is actually informative.
  // "was the cart icon visible" carries zero analytical value; it's
  // visible on every page, always. Clicks on these still fire normally
  // (element_clicked is a separate, independent listener) — this only
  // drops the impression side.
  var IMPRESSION_EXCLUDED_TYPES = {
    cart_icon: true,
    currency_switcher: true,
    footer_link: true,
    social_icon: true,
    store_locator_link: true,
    nav_link: true,
  };

  function wireImpressions(root) {
    root.querySelectorAll("[data-track-type]").forEach(function (el) {
      if (observedElements.has(el)) return;
      if (IMPRESSION_EXCLUDED_TYPES[el.getAttribute("data-track-type")]) return;
      observedElements.add(el);
      impressionObserver.observe(el);
    });
  }

  // ---- element_clicked -----------------------------------------------------

  document.addEventListener("click", function (e) {
    var el = e.target.closest("[data-track-type]");
    if (!el) return;

    var type = el.getAttribute("data-track-type");
    var action = el.getAttribute("data-track-action") || "click";
    var label = deriveElementLabel(el);
    var listCtx = readListContext(el);

    // wishlist_icon is a state toggle whose actual funnel meaning
    // (added vs. removed) matters more than "it was clicked" — was
    // previously falling through to a generic element_clicked with a
    // static action="toggle", never reading the real resulting state at
    // all (this markup uses aria-pressed, not aria-expanded, so the
    // toggle branch below never even matched it). Two dedicated events
    // instead of one ambiguous click.
    if (type === "wishlist_icon") {
      requestAnimationFrame(function () {
        var added = el.getAttribute("aria-pressed") === "true";
        enqueue(
          added ? "wishlist_added" : "wishlist_removed",
          Object.assign(
            {
              element_id: nearestItemId(el),
              element_label: label,
              element_name: buildElementName(type, listCtx.list_id || label),
            },
            productContext(el),
            listCtx
          )
        );
      });
      return;
    }

    // Toggle-style components (accordion sections, panels) report their
    // resulting state via aria-expanded rather than needing a second
    // element_type — read it after the framework's own click handler
    // has had a chance to flip it.
    if (el.hasAttribute("data-track-toggle")) {
      requestAnimationFrame(function () {
        var expanded = el.getAttribute("aria-expanded") === "true";
        enqueue(
          "element_clicked",
          Object.assign(
            {
              element_type: type,
              action: expanded ? "expand" : "collapse",
              element_id: nearestItemId(el),
              element_label: label,
              element_name: buildElementName(type, listCtx.list_id || label),
              value: el.getAttribute("data-track-value") || undefined,
            },
            productContext(el),
            listCtx
          )
        );
      });
      return;
    }

    // element_id used to read data-track-item-id directly off the clicked
    // node — a real bug, since plenty of markup (quick-add/select-options
    // buttons, the coupon "Apply" button) only carries that attribute on a
    // wrapping ancestor, not the clicked element itself. element_viewed's
    // impression handler already climbed ancestors via nearestItemId(); this
    // branch silently didn't, so the exact same instance came back with a
    // populated element_id on view and undefined on click. Now consistent.
    enqueue(
      "element_clicked",
      Object.assign(
        {
          element_type: type,
          action: action,
          element_id: nearestItemId(el),
          element_label: label,
          element_name: buildElementName(type, listCtx.list_id || label),
          value: el.getAttribute("data-track-value") || undefined,
        },
        productContext(el),
        listCtx
      )
    );
  });

  // ---- outbound_link_clicked / file_downloaded --------------------------
  //
  // GA4's Enhanced Measurement auto-tracks both of these without needing
  // per-link instrumentation — same approach here. One delegated listener
  // on every <a href>, classified by comparing the resolved URL's origin
  // against the current page's, and by file extension for downloads.
  // Independent of the data-track-type click listener above — an anchor
  // carrying both still fires its own element_clicked too, this is an
  // additive signal, not a replacement for deliberate instrumentation.

  var DOWNLOAD_EXTENSIONS = ["pdf", "zip", "csv", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "mp4", "mp3"];

  function fileExtension(pathname) {
    var match = pathname.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : undefined;
  }

  document.addEventListener("click", function (e) {
    var anchor = e.target.closest("a[href]");
    if (!anchor) return;
    var url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (err) {
      return; // malformed href (mailto:, tel:, javascript:) — nothing to classify
    }
    var linkText = (anchor.textContent || "").trim().slice(0, 120) || undefined;
    var ext = fileExtension(url.pathname);
    if (ext && DOWNLOAD_EXTENSIONS.indexOf(ext) !== -1) {
      enqueue("file_downloaded", {
        file_name: url.pathname.split("/").pop(),
        file_type: ext,
        link_text: linkText,
      });
      return; // a download link is never also "outbound" in the same sense
    }
    if (url.origin !== window.location.origin) {
      enqueue("outbound_link_clicked", {
        destination_domain: url.hostname,
        link_text: linkText,
      });
    }
  });

  // ---- form_submitted -------------------------------------------------
  //
  // Was designed in the schema but never wired — real gap from the last
  // pass. Two shapes: a real <form data-track-form-type="..."> submit
  // (login, signup), and a field that "submits" by losing focus rather
  // than a form event (the cart order-note textarea — it's part of the
  // whole-cart form, there's no separate submit for just the note).

  // Coupon/bank-offer apply calls are AJAX, not a real <form> submit — the
  // listener below can't see whether the code actually validated
  // server-side, only that a submit/blur happened, so a failed coupon
  // looked identical to a successful one. Whichever script owns the actual
  // apply call (not in this repo — a theme JS asset or an app's own
  // script) should dispatch this once its response comes back:
  //   document.dispatchEvent(new CustomEvent("minaki:form-result", {
  //     detail: { form_type: "gift_card", success: true, reason: undefined }
  //   }));
  // This only relays whatever it's told — the missing half is that one
  // dispatch call living somewhere in the real coupon/gift-card JS.
  document.addEventListener("minaki:form-result", function (e) {
    var detail = e.detail || {};
    enqueue("form_submitted", {
      form_type: detail.form_type,
      success: !!detail.success,
      failure_reason: detail.reason || undefined,
    });
  });

  document.addEventListener("submit", function (e) {
    var form = e.target.closest("[data-track-form-type]");
    if (!form) return;
    enqueue(
      "form_submitted",
      Object.assign(
        {
          form_type: form.getAttribute("data-track-form-type"),
          success: true,
        },
        productContext(form)
      )
    );
  });

  document.addEventListener(
    "blur",
    function (e) {
      var el = e.target;
      if (!el.matches || !el.matches("[data-track-form-type]")) return;
      if (el.tagName !== "TEXTAREA" && el.tagName !== "INPUT") return;
      var value = el.value || "";
      var lastValue = el.getAttribute("data-track-last-value") || "";
      if (value === lastValue) return; // no change since last blur, don't re-fire
      el.setAttribute("data-track-last-value", value);
      if (!value) return; // cleared, not submitted
      enqueue("form_submitted", {
        form_type: el.getAttribute("data-track-form-type"),
        success: true,
        character_count: value.length,
      });
    },
    true // blur doesn't bubble — must listen on the capture phase
  );

  // ---- scroll_depth --------------------------------------------------------

  var scrollTicking = false;
  window.addEventListener("scroll", function () {
    if (scrollTicking) return;
    scrollTicking = true;
    requestAnimationFrame(function () {
      scrollTicking = false;
      var doc = document.documentElement;
      var scrolled = ((doc.scrollTop + window.innerHeight) / doc.scrollHeight) * 100;
      SCROLL_CHECKPOINTS.forEach(function (checkpoint) {
        if (scrolled >= checkpoint && !seenScrollCheckpoints[checkpoint]) {
          seenScrollCheckpoints[checkpoint] = true;
          enqueue("scroll_depth", { depth_pct: checkpoint });
        }
      });
    });
  });

  // ---- page_viewed ---------------------------------------------------------
  //
  // Biggest hole in the whole system before this: session_started only fired
  // once per tab-session, on the FIRST page. Every page after that fired
  // literally nothing unless the shopper also clicked/scrolled/impressioned
  // something. A shopper browsing 10 PDPs without clicking any of them
  // produced ONE event for the entire visit. GA4's automatic page_view (one
  // per load, no interaction required) had no equivalent here — this is it.
  // Deliberately separate from session_started, which still marks "first
  // page of a new session" for entry_page/referrer/UTM attribution.
  //
  // entry_* properties are the journey-attribution answer (see
  // consumeEntryContext() above) — populated only when this page load was
  // reached via a tracked internal list click; undefined on a fresh
  // tab/direct-URL/external-referrer landing, same absent-not-faked
  // convention as everything else here.

  (function () {
    var entry = consumeEntryContext();
    enqueue(
      "page_viewed",
      Object.assign(
        { currency: activeCurrency(), shop_currency: shopCurrency() },
        entry
          ? {
              entry_list_id: entry.list_id,
              entry_list_type: entry.list_type,
              entry_item_type: entry.item_type,
              entry_position: entry.position,
              entry_element_name: entry.element_name,
              entry_source_page_type: entry.source_page_type,
            }
          : {}
      )
    );
  })();

  // ---- session_started -----------------------------------------------------

  if (!sessionStorage.getItem("minaki_session_started")) {
    sessionStorage.setItem("minaki_session_started", "1");
    var campaign = campaignContext() || {};
    enqueue("session_started", {
      entry_page: window.location.pathname,
      referrer_domain: document.referrer ? new URL(document.referrer).hostname : undefined,
      utm_source: campaign.utm_source,
      utm_medium: campaign.utm_medium,
      utm_campaign: campaign.utm_campaign,
    });
  }

  // ---- search_performed ------------------------------------------------
  //
  // Net-new event, added to tracking-plan/schema.json alongside this —
  // Shopify's search results page carries the query in ?q=, nothing in
  // the DOM reliably exposes a result count without knowing this theme's
  // specific results template, so results_count stays optional/best-effort.

  if (pageType() === "search") {
    var searchParams = new URLSearchParams(window.location.search);
    var query = searchParams.get("q");
    if (query) {
      var resultsCountEl = document.querySelector("[data-search-results-count]");
      enqueue("search_performed", {
        query: query,
        results_count: resultsCountEl ? Number(resultsCountEl.getAttribute("data-search-results-count")) : undefined,
      });
    }
  }

  // ---- rage_click --------------------------------------------------------
  //
  // 3+ clicks on the same target within 1.5s — frustration signal (dead
  // button, broken link, unresponsive UI). Keyed by a cheap selector
  // string, not the element itself, so re-renders (theme swaps the DOM
  // node but the "same" logical button) don't reset the count.

  var RAGE_CLICK_THRESHOLD = 3;
  var RAGE_CLICK_WINDOW_MS = 1500;

  document.addEventListener("click", function (e) {
    var el = e.target.closest("button, a, [data-track-type]") || e.target;
    // Was tagName+className for anything without an id/data-track-type —
    // two unrelated buttons sharing one utility class (extremely common:
    // "btn btn-primary" on every generic CTA) collapsed into the same key,
    // so rapid clicks split across two different buttons could fire a false
    // rage_click, or genuine rage on one button could get diluted by an
    // unrelated click elsewhere sharing the class. stableTargetKey keys off
    // the actual DOM node via WeakMap instead, so that collision can't
    // happen regardless of shared classes.
    var selector = stableTargetKey(el);
    var now = Date.now();
    var state = rageClickState[selector] || (rageClickState[selector] = { times: [], fired: false });
    state.times = state.times.filter(function (t) {
      return now - t < RAGE_CLICK_WINDOW_MS;
    });
    state.times.push(now);
    if (state.times.length >= RAGE_CLICK_THRESHOLD && !state.fired) {
      state.fired = true;
      enqueue("rage_click", {
        target_selector: selector,
        click_count: state.times.length,
        window_ms: RAGE_CLICK_WINDOW_MS,
      });
    }
    if (state.times.length < RAGE_CLICK_THRESHOLD) state.fired = false;
  });

  // ---- exit_intent -------------------------------------------------------
  //
  // Desktop-only signal by nature — cursor leaving toward the browser
  // chrome has no mobile equivalent (no mouse). clientY <= 0 on a
  // mousemove/mouseout is the standard heuristic. Fires once per page.

  document.addEventListener("mouseout", function (e) {
    if (exitIntentFired) return;
    if (e.clientY > 0 || e.relatedTarget) return; // relatedTarget set = still inside the doc
    exitIntentFired = true;
    enqueue("exit_intent", {
      time_on_page_ms: Date.now() - pageLoadedAt,
    });
  });

  // ---- add_to_cart / remove_from_cart -----------------------------------
  //
  // DOM-click tracking (data-track-type on a button) can only ever catch
  // buttons this file was actually wired to. On a live storefront that
  // structurally misses every other real path into the cart — sticky
  // add-to-cart bars, upsell/cross-sell apps, quick-buy widgets, anything
  // that calls Shopify's cart AJAX endpoints directly rather than through
  // markup this file recognizes. Those endpoints (/cart/add.js,
  // /cart/change.js, /cart/update.js) are Shopify's own public contract —
  // every one of those paths goes through them regardless of which UI
  // triggered it. Wrapping fetch once here catches all of them uniformly,
  // with the real quantity/variant/price Shopify's response returns,
  // instead of best-guessing from whatever's on the clicked element.
  //
  // More invasive than everything else in this file (a global fetch
  // override, site-wide) — flagging that plainly rather than burying it.
  // Scoped tightly to the two URL patterns below; every other fetch call
  // passes through untouched, synchronously, before this ever inspects the
  // response. Spike it against a real cart before trusting it in production,
  // same standing the file already holds the Buy-It-Now bridge to.

  var nativeFetch = window.fetch;
  if (typeof nativeFetch === "function") {
    window.fetch = function (input, init) {
      var promise = nativeFetch.apply(window, arguments);
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var isCartAdd = /\/cart\/add\.js(\?|$)/.test(url);
      var isCartChange = /\/cart\/(change|update)\.js(\?|$)/.test(url);
      if (!isCartAdd && !isCartChange) return promise;

      promise
        .then(function (r) {
          return r.clone().json();
        })
        .then(function (data) {
          if (isCartAdd) {
            // /cart/add.js's single-item response vs a form posting items[]
            // and getting {items:[...]} back — cover both response shapes.
            var items = data && data.items ? data.items : data && data.id ? [data] : [];
            items.forEach(function (item) {
              enqueue("add_to_cart", {
                product_id: item.product_id,
                variant_id: item.variant_id,
                product_name: item.product_title,
                product_sku: item.sku,
                product_price: item.price,
                product_price_shop_currency: shopCurrencyPrice(item.price),
                quantity: item.quantity,
                currency: activeCurrency(),
                shop_currency: shopCurrency(),
              });
            });
          } else if (isCartChange && init && init.body) {
            // /cart/update.js is also how the identity bridge above sets
            // the _anon_id cart attribute — that call has no line-item
            // quantity in its body, so it correctly falls through here
            // without firing a spurious remove_from_cart.
            try {
              var body = JSON.parse(init.body);
              if (body && "quantity" in body && Number(body.quantity) === 0) {
                enqueue("remove_from_cart", {
                  variant_id: body.id,
                  quantity: 0,
                  currency: activeCurrency(),
                  shop_currency: shopCurrency(),
                });
              }
            } catch (e) {
              /* non-JSON body — nothing to parse, nothing to report */
            }
          }
        })
        .catch(function () {
          /* additive signal — never let a parse failure surface anywhere */
        });
      return promise;
    };
  }

  // ---- error_occurred ----------------------------------------------------
  //
  // Zero error visibility existed before this. A broken PDP variant picker
  // or a checkout JS crash showed up only as an unexplained dip in whatever
  // metric it happened to break, nothing pointing at why. Two listeners —
  // thrown errors, and unhandled promise rejections (async failures never
  // reach window.onerror on their own).

  window.addEventListener("error", function (e) {
    enqueue("error_occurred", {
      message: (e.message || "").slice(0, 500),
      filename: e.filename || undefined,
      lineno: e.lineno || undefined,
      colno: e.colno || undefined,
    });
  });

  window.addEventListener("unhandledrejection", function (e) {
    var reason = e.reason;
    var message = reason && reason.message ? reason.message : String(reason);
    enqueue("error_occurred", {
      message: ("unhandled_rejection: " + message).slice(0, 500),
    });
  });

  // ---- web_vital (LCP, CLS) ----------------------------------------------
  //
  // page_load_ms (pageContext(), Navigation Timing) was the only performance
  // signal that existed — one crude number, no distribution, no correlation
  // to drop-off. LCP and CLS are the two Core Web Vitals PerformanceObserver
  // exposes natively with no library needed. Hand-rolled rather than pulling
  // in the `web-vitals` package since this file ships as one plain <script>
  // tag, no bundler step. INP is the one CWV genuinely hard to get right by
  // hand (needs Event Timing API interaction bucketing) — flagged as a
  // follow-up, not implemented here.

  if (window.PerformanceObserver) {
    var lastLCP = null;
    try {
      new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        lastLCP = entries[entries.length - 1];
      }).observe({ type: "largest-contentful-paint", buffered: true });
    } catch (e) {
      /* not supported in this browser — LCP stays unmeasured, not faked */
    }

    var clsValue = 0;
    try {
      new PerformanceObserver(function (list) {
        list.getEntries().forEach(function (entry) {
          if (!entry.hadRecentInput) clsValue += entry.value;
        });
      }).observe({ type: "layout-shift", buffered: true });
    } catch (e) {
      /* not supported — CLS stays unmeasured, not faked */
    }

    var vitalsReported = false;
    function reportVitals() {
      if (vitalsReported) return;
      vitalsReported = true;
      if (lastLCP) enqueue("web_vital", { metric_name: "LCP", value: Math.round(lastLCP.startTime) });
      enqueue("web_vital", { metric_name: "CLS", value: Math.round(clsValue * 1000) / 1000 });
    }

    // LCP/CLS aren't final until the page is actually going away — reporting
    // at pagehide/hidden is the standard pattern here, not this file's own
    // invention.
    document.addEventListener("visibilitychange", function () {
      if (document.visibilityState === "hidden") reportVitals();
    });
    window.addEventListener("pagehide", reportVitals);
  }

  // ---- boot ------------------------------------------------------------

  wireImpressions(document);

  // Sections that render after load (tab switches, infinite/load-more,
  // quick-add modals) need the same treatment without a page reload —
  // watch the DOM for new [data-track-type] nodes and wire them the
  // same way, once, automatically.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) wireImpressions(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  // pagehide, not beforeunload — beforeunload is legacy and documented
  // (MDN, web.dev) to disqualify the page from the back-forward cache in
  // Safari and Firefox, meaning every navigation on this site was paying
  // that bfcache penalty just to keep this handler alive. pagehide plus the
  // existing visibilitychange cover the same "page is going away" moment
  // without that cost.
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "hidden") flush();
  });
})();
